import { lazy, Suspense, useId, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  CircularProgress,
  Link,
  Stack,
  TextField
} from '@mui/material';

const SALES_EMAIL = 'info@askpot.com';
const MultiStepSignupOverlay = lazy(() => import('components/auth/MultiStepSignupOverlay'));

const SalesContactDialog = ({ onClose, user, source, bookingError }) => {
  const titleId = useId();
  const [details, setDetails] = useState({
    name: user?.name || '',
    email: user?.email || '',
    company: '',
    message: ''
  });
  const [errors, setErrors] = useState({});
  const [requestSent, setRequestSent] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [showSignupOverlay, setShowSignupOverlay] = useState(false);
  const submittingRef = useRef(false);

  const updateField = (field) => (event) => {
    setDetails((previous) => ({ ...previous, [field]: event.target.value }));
    setErrors((previous) => ({ ...previous, [field]: '' }));
  };

  const submitRequest = async (values) => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    setIsSubmitting(true);
    setSubmitError('');
    try {
      // The existing endpoint requires an account. Keep the form while signing in.
      const { getAuth } = await import('firebase/auth');
      if (!getAuth().currentUser) {
        setShowSignupOverlay(true);
        return;
      }
      const { sendSalesRequest } = await import('services/salesContactService');
      await sendSalesRequest({ ...values, source });
      setRequestSent(true);
    } catch (error) {
      setSubmitError('We could not send your request. Please try again.');
    } finally {
      submittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (submittingRef.current || requestSent) return;
    const trimmed = Object.fromEntries(Object.entries(details).map(([key, value]) => [key, value.trim()]));
    const nextErrors = {};
    if (!trimmed.name) nextErrors.name = 'Enter your name.';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed.email)) nextErrors.email = 'Enter a valid email address.';
    if (!trimmed.company) nextErrors.company = 'Enter your company name.';
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;
    setDetails(trimmed);
    await submitRequest(trimmed);
  };

  const handleSignupComplete = () => {
    setShowSignupOverlay(false);
    submitRequest(details);
  };

  return (
    <>
      <Dialog open={!showSignupOverlay} onClose={isSubmitting ? undefined : onClose} maxWidth="sm" fullWidth aria-labelledby={titleId}>
        <Box component="form" noValidate onSubmit={handleSubmit}>
          <DialogTitle id={titleId}>{requestSent ? 'Your request has been sent' : 'Contact Sales'}</DialogTitle>
          <DialogContent>
            {requestSent ? (
              <>
                <DialogContentText sx={{ mb: 2 }}>Thank you! Our team will get back to you by email.</DialogContentText>
                <DialogContentText>
                  If you need to reach us quickly, email <Link href={`mailto:${SALES_EMAIL}`}>{SALES_EMAIL}</Link>.
                </DialogContentText>
              </>
            ) : (
              <>
                {bookingError && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Meeting booking is currently unavailable. You can contact our team by email instead.
                  </Alert>
                )}
                <DialogContentText sx={{ mb: 2 }}>Tell us a little about your team and we will get back to you by email.</DialogContentText>
                {submitError && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {submitError}
                  </Alert>
                )}
                <Stack component="fieldset" disabled={isSubmitting} spacing={2} sx={{ p: 0, pt: 1, m: 0, border: 0, minWidth: 0 }}>
                  <TextField
                    autoFocus
                    required
                    fullWidth
                    label="Name"
                    name="name"
                    autoComplete="name"
                    value={details.name}
                    onChange={updateField('name')}
                    error={Boolean(errors.name)}
                    helperText={errors.name}
                    inputProps={{ maxLength: 120 }}
                  />
                  <TextField
                    required
                    fullWidth
                    label="Work email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    value={details.email}
                    onChange={updateField('email')}
                    error={Boolean(errors.email)}
                    helperText={errors.email}
                    inputProps={{ maxLength: 254 }}
                  />
                  <TextField
                    required
                    fullWidth
                    label="Company"
                    name="company"
                    autoComplete="organization"
                    value={details.company}
                    onChange={updateField('company')}
                    error={Boolean(errors.company)}
                    helperText={errors.company}
                    inputProps={{ maxLength: 160 }}
                  />
                  <TextField
                    fullWidth
                    multiline
                    minRows={2}
                    label="What do you need? (optional)"
                    name="message"
                    value={details.message}
                    onChange={updateField('message')}
                    inputProps={{ maxLength: 2000 }}
                  />
                </Stack>
              </>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3 }}>
            {requestSent ? (
              <>
                <Button onClick={onClose} color="secondary">
                  Close
                </Button>
              </>
            ) : (
              <>
                <Button onClick={onClose} color="secondary" disabled={isSubmitting}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={isSubmitting}
                  startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : null}
                >
                  {isSubmitting ? 'Sending...' : 'Send request'}
                </Button>
              </>
            )}
          </DialogActions>
        </Box>
      </Dialog>
      {showSignupOverlay && (
        <Suspense
          fallback={
            <Dialog open>
              <DialogContent>Loading sign in...</DialogContent>
            </Dialog>
          }
        >
          <MultiStepSignupOverlay open onComplete={handleSignupComplete} onClose={() => setShowSignupOverlay(false)} allowBackgroundClose />
        </Suspense>
      )}
    </>
  );
};

SalesContactDialog.propTypes = {
  onClose: PropTypes.func.isRequired,
  user: PropTypes.shape({ name: PropTypes.string, email: PropTypes.string }),
  source: PropTypes.string.isRequired,
  bookingError: PropTypes.bool
};

export default SalesContactDialog;
